import dayjs from 'dayjs'
import {http} from 'hiya-common/src/utils/axios'

import type {WorldCupBetPayType} from './types'

/** 货币类型：1 金币，2 免费票 */
export const WORLD_CUP_PICK_BET_CURRENCY = {
  coins: 1,
  vouchers: 2,
} as const

export type WorldCupPickBetCurrencyType =
  typeof WORLD_CUP_PICK_BET_CURRENCY[keyof typeof WORLD_CUP_PICK_BET_CURRENCY]

export interface WorldCupPickBetRequest {
  team_id: number
  currency_type: WorldCupPickBetCurrencyType
  amount: number
  /** 当前阶段序号，来自 homepage cur_stage_no */
  stage_no: number
}

export interface WorldCupPickBetResponse {
  err_msg?: string
  add_votes?: number
  user_team_votes?: number
  user_coins?: number
  user_free_tickets?: number
  pool_coins?: number
}

export type NormalizedWorldCupPickBetData = {
  err_msg: string
  add_votes: number
  user_team_votes: number
  user_coins: number
  user_free_tickets: number
  pool_coins: number
}

export const MOCK_WORLD_CUP_PICK_BET_REQUEST: WorldCupPickBetRequest = {
  team_id: 1,
  currency_type: 2,
  amount: 1,
  stage_no: 1,
}

export const MOCK_WORLD_CUP_PICK_BET_RESPONSE: WorldCupPickBetResponse = {
  err_msg: '',
  add_votes: 10,
  user_team_votes: 60,
  user_coins: 4500,
  user_free_tickets: 1,
  pool_coins: 1000500,
}

export function mapPayTypeToCurrencyType(
  payType: WorldCupBetPayType
): WorldCupPickBetCurrencyType {
  return WORLD_CUP_PICK_BET_CURRENCY[payType]
}

export function normalizeWorldCupPickBetResponse(
  res: WorldCupPickBetResponse
): NormalizedWorldCupPickBetData {
  return {
    err_msg: res?.err_msg ?? '',
    add_votes: res?.add_votes ?? 0,
    user_team_votes: res?.user_team_votes ?? 0,
    user_coins: res?.user_coins ?? 0,
    user_free_tickets: res?.user_free_tickets ?? 0,
    pool_coins: res?.pool_coins ?? 0,
  }
}

export async function postWorldCupPickBet(
  params: WorldCupPickBetRequest
): Promise<NormalizedWorldCupPickBetData> {
  const res = await http.post<{data: WorldCupPickBetResponse}>(
    '/live/activity/world_cup_pick_bet',
    {...params},
    {
      store: window.store,
    }
  )
  return normalizeWorldCupPickBetResponse(res.data.data)
}

export type WorldCupPickBetPreviewRequest = Omit<
  WorldCupPickBetRequest,
  'stage_no'
>

export interface WorldCupPickBetPreviewResponse {
  err_msg?: string
  team_id?: number
  team_name?: string
  currency_type?: WorldCupPickBetCurrencyType
  amount?: number
  votes_per_coin?: number
  add_votes?: number
  expect_coins?: number
  next_stage_votes_per_coin?: number
  next_stage_start_time?: number
}

export type NormalizedWorldCupPickBetPreviewData = {
  err_msg: string
  team_id: number
  team_name: string
  currency_type: WorldCupPickBetCurrencyType
  amount: number
  votes_per_coin: number
  add_votes: number
  expect_coins: number
  next_stage_votes_per_coin: number
  next_stage_start_time: number
}

export const MOCK_WORLD_CUP_PICK_BET_PREVIEW_RESPONSE: WorldCupPickBetPreviewResponse =
  {
    err_msg: '',
    team_id: 1,
    team_name: 'Brazil',
    currency_type: 1,
    amount: 500,
    votes_per_coin: 10,
    add_votes: 5000,
    expect_coins: 120000,
    next_stage_votes_per_coin: 8,
    next_stage_start_time: 1717286400,
  }

export function formatNextStageEffectiveAt(timestampSec: number): string {
  if (!timestampSec) {
    return ''
  }
  return dayjs(timestampSec * 1000).format('MM-DD HH:mm')
}

export function normalizeWorldCupPickBetPreviewResponse(
  res: WorldCupPickBetPreviewResponse
): NormalizedWorldCupPickBetPreviewData {
  return {
    err_msg: res?.err_msg ?? '',
    team_id: res?.team_id ?? 0,
    team_name: res?.team_name ?? '',
    currency_type: res?.currency_type ?? WORLD_CUP_PICK_BET_CURRENCY.coins,
    amount: res?.amount ?? 0,
    votes_per_coin: res?.votes_per_coin ?? 0,
    add_votes: res?.add_votes ?? 0,
    expect_coins: res?.expect_coins ?? 0,
    next_stage_votes_per_coin: res?.next_stage_votes_per_coin ?? 0,
    next_stage_start_time: res?.next_stage_start_time ?? 0,
  }
}

export async function postWorldCupPickBetPreview(
  params: WorldCupPickBetPreviewRequest
): Promise<NormalizedWorldCupPickBetPreviewData> {
  const res = await http.post<{data: WorldCupPickBetPreviewResponse}>(
    '/live/activity/world_cup_pick_bet_preview',
    {...params},
    {
      store: window.store,
    }
  )
  return normalizeWorldCupPickBetPreviewResponse(res.data.data)
}
